// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useRef } from 'react';
import type { EiReport, EiReportingPeriod, GuidedPeriodInfo } from 'employment-insurance-data-access';
import type { GuidedTextResolver } from './guided-text';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const day = (iso: string, offset = 0) => new Date(new Date(iso).getTime() + offset * MS_PER_DAY).toISOString().slice(0, 10);

export interface ReportSubmittedProps {
  report: EiReport;
  period: GuidedPeriodInfo;
  /** The schedule's next period, when there is one. */
  nextPeriod: EiReportingPeriod | undefined;
  t: GuidedTextResolver;
  onDone: () => void;
}

/**
 * The confirmation after Submit (content map §10): "Thank you…", "We've
 * received your report", "Your next report" and "Your claim details", then
 * back to the report overview, where the row now reads Submitted. The
 * prototype's "(simulated)" wording isn't carried over, and its "Start your
 * next report" button isn't ported.
 */
export function ReportSubmitted({ report, period, nextPeriod, t, onDone }: ReportSubmittedProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section className="ei-guided-done" aria-labelledby="ei-guided-done-title" data-testid="guided-submitted">
      <h1 id="ei-guided-done-title" ref={headingRef} tabIndex={-1} className="ei-guided__title">
        {t({ key: 'confirm.title' })}
      </h1>
      <p className="ei-guided__subtitle">{t({ key: 'confirm.submitted', params: { date: { t: 'date', v: day(report.submittedAt) } } })}</p>

      <div className="ei-guided-done__card ei-guided-done__card--ok">
        <span className="ei-guided-done__ok" aria-hidden="true">
          ✓
        </span>
        <div>
          <h2 className="ei-guided-done__heading">{t({ key: 'confirm.receivedHeading' })}</h2>
          <p>{t({ key: 'confirm.receivedBody', params: { periodEnd: { t: 'date', v: period.lastDay } } })}</p>
        </div>
      </div>

      {nextPeriod ? (
        <div className="ei-guided-done__card">
          <h2 className="ei-guided-done__heading">{t({ key: 'confirm.nextHeading' })}</h2>
          <p>
            {t({
              key: 'confirm.nextBody',
              params: { nextPeriod: { t: 'range', from: day(nextPeriod.periodStart), to: day(nextPeriod.periodEnd, -1) } },
            })}
          </p>
        </div>
      ) : null}

      <div className="ei-guided-done__card">
        <h2 className="ei-guided-done__heading">{t({ key: 'confirm.claimHeading' })}</h2>
        <p>{t({ key: 'confirm.claimBody' })}</p>
      </div>

      <div className="ei-guided-review__actions">
        <scds-button variant="primary" type="button" data-testid="guided-back-to-reports" onClick={onDone}>
          {t({ key: 'confirm.backToReports' })}
        </scds-button>
      </div>
    </section>
  );
}
